import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import Swal from 'sweetalert2';

const TIPOS = ['comercial', 'industrial', 'servicios', 'constructora', 'agropecuaria', 'otro'];

const FORM_DEFAULT = {
  nombre: '', rnc: '', tipo_empresa: 'comercial',
  email: '', telefono: '', direccion: '', contacto: '', notas: '',
  pin: '', pin_confirmar: '',
};

// ─── Clave localStorage para bloqueo de contraseña ───────────────────────────
const BLOQUEO_KEY = 'pin_bloqueo_hasta';

const inputSty = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const labelSty = { display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, color: '#374151' };

function F({ label, field, type = 'text', placeholder = '', form, setForm }) {
  return (
    <div>
      <label style={labelSty}>{label}</label>
      <input style={inputSty} type={type} placeholder={placeholder}
        value={form[field]} onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))} />
    </div>
  );
}

// ─── Modal PIN (selección de empresa) ────────────────────────────────────────
function ModalPin({ empresa, onExito, onCancelar }) {
  const [fase, setFase]           = useState('pin');   // 'pin' | 'password' | 'nuevo_pin'
  const [pin, setPin]             = useState('');
  const [password, setPassword]   = useState('');
  const [nuevoPin, setNuevoPin]   = useState('');
  const [nuevoPin2, setNuevoPin2] = useState('');
  const [intentos, setIntentos]   = useState(0);
  const [error, setError]         = useState('');
  const [cargando, setCargando]   = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, [fase]);

  // Verificar si hay bloqueo activo
  const bloqueadoHasta = () => {
    const ts = localStorage.getItem(BLOQUEO_KEY);
    if (!ts) return null;
    const hasta = parseInt(ts, 10);
    return Date.now() < hasta ? hasta : null;
  };

  const minutosBloqueo = () => {
    const hasta = bloqueadoHasta();
    if (!hasta) return 0;
    return Math.ceil((hasta - Date.now()) / 60000);
  };

  // ── Verificar PIN ──
  const handleVerificarPin = async () => {
    if (!/^\d{4}$/.test(pin)) { setError('El PIN debe ser exactamente 4 dígitos.'); return; }
    setCargando(true); setError('');
    try {
      await api.post(`/empresas-contables/${empresa.id}/verificar-pin`, { pin });
      onExito(empresa);
    } catch (err) {
      const nuevosIntentos = intentos + 1;
      setIntentos(nuevosIntentos);
      setPin('');
      if (nuevosIntentos >= 3) {
        setError('PIN incorrecto. 3 intentos fallidos.');
      } else {
        setError(`PIN incorrecto. Intento ${nuevosIntentos}/3.`);
      }
    } finally { setCargando(false); }
  };

  // ── Verificar contraseña admin ──
  const handleVerificarPassword = async () => {
    if (!password) { setError('Ingresa tu contraseña.'); return; }
    const hasta = bloqueadoHasta();
    if (hasta) { setError(`Bloqueado por ${minutosBloqueo()} min más.`); return; }
    setCargando(true); setError('');
    try {
      await api.post('/empresas-contables/verificar-password', { password });
      setFase('nuevo_pin');
      setPassword('');
      setError('');
    } catch (err) {
      setPassword('');
      if (err.response?.status === 400) {
        // Bloquear por 5 minutos
        localStorage.setItem(BLOQUEO_KEY, String(Date.now() + 5 * 60 * 1000));
        setError('Contraseña incorrecta. Bloqueado 5 minutos por seguridad.');
      } else {
        setError('No se pudo verificar la contraseña.');
      }
    } finally { setCargando(false); }
  };

  // ── Configurar nuevo PIN tras recuperación ──
  const handleConfigurarPin = async () => {
    if (!/^\d{4}$/.test(nuevoPin))   { setError('El PIN debe ser 4 dígitos numéricos.'); return; }
    if (nuevoPin !== nuevoPin2)       { setError('Los PINs no coinciden.'); return; }
    setCargando(true); setError('');
    try {
      await api.post(`/empresas-contables/${empresa.id}/configurar-pin`, { pin: nuevoPin });
      localStorage.removeItem(BLOQUEO_KEY);
      onExito(empresa);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo guardar el PIN.');
    } finally { setCargando(false); }
  };

  const ovSty = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' };
  const modSty = { background: '#fff', borderRadius: 14, padding: 32, width: 360, boxShadow: '0 8px 32px rgba(0,0,0,.18)' };
  const pinInputSty = { ...inputSty, fontSize: 24, letterSpacing: 12, textAlign: 'center', fontFamily: 'monospace', fontWeight: 700 };
  const btnPrimSty = { width: '100%', padding: '10px 0', background: '#1e3a5f', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer', marginTop: 8 };
  const btnSecSty  = { width: '100%', padding: '8px 0', background: 'none', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer', marginTop: 6 };
  const errSty = { color: '#dc2626', fontSize: 12, marginTop: 8, textAlign: 'center', minHeight: 18 };

  return (
    <div style={ovSty} onClick={e => { if (e.target === e.currentTarget) onCancelar(); }}>
      <div style={modSty}>

        {/* ── Fase PIN ── */}
        {fase === 'pin' && (
          <>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🔐</div>
              <h3 style={{ margin: 0, fontSize: 17, color: '#1e3a5f' }}>PIN de acceso</h3>
              <p style={{ margin: '6px 0 0', fontSize: 13, color: '#64748b' }}>
                <strong>{empresa.nombre}</strong>
              </p>
            </div>
            <label style={labelSty}>Ingresa el PIN de 4 dígitos</label>
            <input
              ref={inputRef}
              style={pinInputSty}
              type="password"
              inputMode="numeric"
              maxLength={4}
              placeholder="••••"
              value={pin}
              onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              onKeyDown={e => e.key === 'Enter' && handleVerificarPin()}
            />
            <p style={errSty}>{error}</p>
            <button style={btnPrimSty} onClick={handleVerificarPin} disabled={cargando || pin.length !== 4}>
              {cargando ? 'Verificando...' : 'Confirmar'}
            </button>
            {intentos >= 3 && (
              <button style={{ ...btnSecSty, color: '#b45309', borderColor: '#fcd34d', marginTop: 10 }}
                onClick={() => { setFase('password'); setError(''); setPin(''); }}>
                ¿Olvidaste el PIN? Usar contraseña
              </button>
            )}
            <button style={btnSecSty} onClick={onCancelar}>Cancelar</button>
          </>
        )}

        {/* ── Fase contraseña admin ── */}
        {fase === 'password' && (
          <>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🔑</div>
              <h3 style={{ margin: 0, fontSize: 17, color: '#92400e' }}>Verificación de administrador</h3>
              <p style={{ margin: '6px 0 0', fontSize: 13, color: '#64748b' }}>
                Ingresa tu contraseña de administrador para acceder a <strong>{empresa.nombre}</strong> y configurar un nuevo PIN.
              </p>
            </div>
            {bloqueadoHasta() ? (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 16px', textAlign: 'center' }}>
                <p style={{ margin: 0, color: '#dc2626', fontWeight: 600, fontSize: 14 }}>
                  🔒 Bloqueado por {minutosBloqueo()} minuto{minutosBloqueo() !== 1 ? 's' : ''} más.
                </p>
                <p style={{ margin: '4px 0 0', color: '#9ca3af', fontSize: 12 }}>
                  Contraseña incorrecta ingresada previamente.
                </p>
              </div>
            ) : (
              <>
                <label style={labelSty}>Contraseña de administrador</label>
                <input
                  ref={inputRef}
                  style={inputSty}
                  type="password"
                  placeholder="Tu contraseña"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleVerificarPassword()}
                />
              </>
            )}
            <p style={errSty}>{error}</p>
            {!bloqueadoHasta() && (
              <button style={{ ...btnPrimSty, background: '#92400e' }} onClick={handleVerificarPassword} disabled={cargando || !password}>
                {cargando ? 'Verificando...' : 'Verificar contraseña'}
              </button>
            )}
            <button style={btnSecSty} onClick={() => { setFase('pin'); setError(''); setPassword(''); }}>
              ← Volver al PIN
            </button>
            <button style={btnSecSty} onClick={onCancelar}>Cancelar</button>
          </>
        )}

        {/* ── Fase nuevo PIN ── */}
        {fase === 'nuevo_pin' && (
          <>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🆕</div>
              <h3 style={{ margin: 0, fontSize: 17, color: '#059669' }}>Configurar nuevo PIN</h3>
              <p style={{ margin: '6px 0 0', fontSize: 13, color: '#64748b' }}>
                Define un nuevo PIN de 4 dígitos para <strong>{empresa.nombre}</strong>.
              </p>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelSty}>Nuevo PIN (4 dígitos)</label>
              <input
                ref={inputRef}
                style={pinInputSty}
                type="password"
                inputMode="numeric"
                maxLength={4}
                placeholder="••••"
                value={nuevoPin}
                onChange={e => setNuevoPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              />
            </div>
            <div style={{ marginBottom: 4 }}>
              <label style={labelSty}>Confirmar nuevo PIN</label>
              <input
                style={pinInputSty}
                type="password"
                inputMode="numeric"
                maxLength={4}
                placeholder="••••"
                value={nuevoPin2}
                onChange={e => setNuevoPin2(e.target.value.replace(/\D/g, '').slice(0, 4))}
                onKeyDown={e => e.key === 'Enter' && handleConfigurarPin()}
              />
            </div>
            <p style={errSty}>{error}</p>
            <button style={{ ...btnPrimSty, background: '#059669' }}
              onClick={handleConfigurarPin}
              disabled={cargando || nuevoPin.length !== 4 || nuevoPin2.length !== 4}>
              {cargando ? 'Guardando...' : '✓ Guardar PIN y acceder'}
            </button>
            <button style={btnSecSty} onClick={onCancelar}>Cancelar</button>
          </>
        )}

      </div>
    </div>
  );
}

// ─── Modal configurar PIN (desde botón en tabla) ──────────────────────────────
function ModalConfigurarPin({ empresa, onExito, onCancelar }) {
  const [pin, setPin]   = useState('');
  const [pin2, setPin2] = useState('');
  const [error, setError]   = useState('');
  const [cargando, setCarg] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const guardar = async () => {
    if (!/^\d{4}$/.test(pin))  { setError('El PIN debe ser 4 dígitos numéricos.'); return; }
    if (pin !== pin2)           { setError('Los PINs no coinciden.'); return; }
    setCarg(true); setError('');
    try {
      await api.post(`/empresas-contables/${empresa.id}/configurar-pin`, { pin });
      onExito();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo guardar el PIN.');
    } finally { setCarg(false); }
  };

  const ovSty  = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' };
  const modSty = { background: '#fff', borderRadius: 14, padding: 32, width: 340, boxShadow: '0 8px 32px rgba(0,0,0,.18)' };
  const pinSty = { ...inputSty, fontSize: 24, letterSpacing: 12, textAlign: 'center', fontFamily: 'monospace', fontWeight: 700 };

  return (
    <div style={ovSty} onClick={e => { if (e.target === e.currentTarget) onCancelar(); }}>
      <div style={modSty}>
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🔐</div>
          <h3 style={{ margin: 0, fontSize: 17, color: '#1e3a5f' }}>
            {empresa.tiene_pin ? 'Cambiar PIN' : 'Configurar PIN'}
          </h3>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: '#64748b' }}><strong>{empresa.nombre}</strong></p>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={labelSty}>Nuevo PIN (4 dígitos)</label>
          <input ref={inputRef} style={pinSty} type="password" inputMode="numeric" maxLength={4}
            placeholder="••••" value={pin}
            onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))} />
        </div>
        <div style={{ marginBottom: 4 }}>
          <label style={labelSty}>Confirmar PIN</label>
          <input style={pinSty} type="password" inputMode="numeric" maxLength={4}
            placeholder="••••" value={pin2}
            onChange={e => setPin2(e.target.value.replace(/\D/g, '').slice(0, 4))}
            onKeyDown={e => e.key === 'Enter' && guardar()} />
        </div>
        {error && <p style={{ color: '#dc2626', fontSize: 12, marginTop: 8, textAlign: 'center' }}>{error}</p>}
        <button onClick={guardar} disabled={cargando || pin.length !== 4 || pin2.length !== 4}
          style={{ width: '100%', padding: '10px 0', background: '#1e3a5f', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer', marginTop: 12 }}>
          {cargando ? 'Guardando...' : '✓ Guardar PIN'}
        </button>
        <button onClick={onCancelar}
          style={{ width: '100%', padding: '8px 0', background: 'none', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer', marginTop: 6 }}>
          Cancelar
        </button>
      </div>
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────
export default function EmpresasContables() {
  const [empresas, setEmpresas]         = useState([]);
  const [limite, setLimite]             = useState(1);
  const [uso, setUso]                   = useState(0);
  const [modalOpen, setModalOpen]       = useState(false);
  const [editando, setEditando]         = useState(null);
  const [form, setForm]                 = useState(FORM_DEFAULT);
  const [logoFile, setLogoFile]         = useState(null);   // File a subir
  const [logoPreview, setLogoPreview]   = useState(null);   // objectURL local (antes de guardar)
  const [logoActual, setLogoActual]     = useState(null);   // { path, v } del logo ya guardado (edición)
  const [loading, setLoading]           = useState(false);
  // PIN flows
  const [modalPin, setModalPin]         = useState(null);   // empresa o null
  const [modalConfigPin, setModalConfigPin] = useState(null); // empresa o null

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/empresas-contables');
      const lista = r.data.empresas || r.data;
      setEmpresas(lista);
      setLimite(r.data.limite ?? 1);
      setUso(r.data.uso ?? 0);
      const activas = Array.isArray(lista) ? lista.filter(e => e.activa) : [];
      if (activas.length === 1 && !localStorage.getItem('empresa_contable_id')) {
        localStorage.setItem('empresa_contable_id', activas[0].id);
        localStorage.setItem('empresa_contable_nombre', activas[0].nombre);
      }
    } catch (e) {
      Swal.fire('Error', e.response?.data?.error || e.message, 'error');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirCrear = () => {
    setEditando(null); setForm(FORM_DEFAULT);
    setLogoFile(null); setLogoPreview(null); setLogoActual(null);
    setModalOpen(true);
  };

  const abrirEditar = (emp) => {
    setEditando(emp.id);
    setForm({
      nombre: emp.nombre || '', rnc: emp.rnc || '',
      tipo_empresa: emp.tipo_empresa || 'comercial',
      email: emp.email || '', telefono: emp.telefono || '',
      direccion: emp.direccion || '', contacto: emp.contacto || '', notas: emp.notas || '',
      pin: '', pin_confirmar: '',
    });
    setLogoFile(null); setLogoPreview(null);
    setLogoActual(emp.logo_path
      ? { path: emp.logo_path, v: new Date(emp.logo_actualizado || Date.now()).getTime() }
      : null);
    setModalOpen(true);
  };

  const onLogoChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(f.type)) {
      Swal.fire('Formato inválido', 'Solo PNG, JPG o WebP', 'warning'); return;
    }
    if (f.size > 2 * 1024 * 1024) {
      Swal.fire('Muy grande', 'El logo debe pesar máximo 2 MB', 'warning'); return;
    }
    setLogoFile(f);
    setLogoPreview(URL.createObjectURL(f));   // preview inmediato
  };

  const guardar = async () => {
    if (!form.nombre.trim()) { Swal.fire('Validación', 'El nombre es requerido', 'warning'); return; }

    // Validar PIN solo al crear
    if (!editando) {
      if (!/^\d{4}$/.test(form.pin)) {
        Swal.fire('PIN requerido', 'El PIN debe ser exactamente 4 dígitos numéricos.', 'warning');
        return;
      }
      if (form.pin !== form.pin_confirmar) {
        Swal.fire('PIN no coincide', 'Los dos PINs ingresados no son iguales.', 'warning');
        return;
      }
    }

    try {
      // Un solo request multipart con todos los campos + logo opcional
      const { pin_confirmar, pin, ...campos } = form;
      const datos = editando ? campos : { ...campos, pin }; // el pin solo se manda al crear
      const fd = new FormData();
      Object.entries(datos).forEach(([k, v]) => fd.append(k, v ?? ''));
      if (logoFile) fd.append('logo', logoFile);

      const cfg = { headers: { 'Content-Type': 'multipart/form-data' } };
      if (editando) await api.put(`/empresas-contables/${editando}`, fd, cfg);
      else          await api.post('/empresas-contables', fd, cfg);

      setModalOpen(false);
      cargar();
      Swal.fire('Listo', editando ? 'Empresa actualizada' : 'Empresa creada', 'success');
    } catch (e) {
      Swal.fire('Error', e.response?.data?.error || e.message, 'error');
    }
  };

  const desactivar = async (emp) => {
    const { isConfirmed } = await Swal.fire({
      title: 'Desactivar empresa',
      text: `Confirma desactivar "${emp.nombre}"?`,
      icon: 'warning', showCancelButton: true,
      confirmButtonText: 'Si, desactivar', cancelButtonText: 'Cancelar',
    });
    if (!isConfirmed) return;
    try {
      await api.delete(`/empresas-contables/${emp.id}`);
      cargar();
    } catch (e) {
      Swal.fire('Error', e.response?.data?.error || e.message, 'error');
    }
  };

  // ── Seleccionar empresa: verificar PIN ──
  const seleccionarEmpresa = (emp) => {
    if (!emp.tiene_pin) {
      // Sin PIN configurado → ir directo a configurarlo
      setModalConfigPin(emp);
      return;
    }
    setModalPin(emp);
  };

  const onPinExito = (emp) => {
    setModalPin(null);
    localStorage.setItem('empresa_contable_id',    emp.id);
    localStorage.setItem('empresa_contable_nombre', emp.nombre);
    Swal.fire({
      title: 'Empresa activa',
      text: `Ahora trabajas con: ${emp.nombre}`,
      icon: 'success', timer: 2000, showConfirmButton: false,
    });
  };

  const onConfigPinExito = async () => {
    setModalConfigPin(null);
    await cargar(); // esperar a que se actualice tiene_pin antes de que el usuario pueda hacer clic
    Swal.fire('PIN configurado', 'El PIN fue guardado. Ahora puedes seleccionar la empresa.', 'success');
  };

  const _empId = localStorage.getItem('empresa_contable_id');
  const empresaActiva = _empId ? parseInt(_empId) : null;

  const sty = {
    page:    { padding: 24, fontFamily: 'sans-serif' },
    h1:      { margin: '0 0 6px', fontSize: 22, fontWeight: 700 },
    sub:     { color: '#64748b', fontSize: 14, marginBottom: 20 },
    card:    { background: '#fff', borderRadius: 10, boxShadow: '0 1px 6px rgba(0,0,0,.08)', padding: 20, marginBottom: 16 },
    btn:     (c) => ({ padding: '8px 16px', border: 'none', borderRadius: 6, cursor: 'pointer', background: c, color: '#fff', fontWeight: 600, fontSize: 13 }),
    tbl:     { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th:      { padding: '9px 12px', background: '#f1f5f9', fontWeight: 600, textAlign: 'left', borderBottom: '2px solid #e2e8f0' },
    td:      { padding: '9px 12px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' },
    badge:   (c) => ({ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: c + '22', color: c }),
    input:   { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' },
    label:   { display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, color: '#374151' },
    grid2:   { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 12 },
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
    modal:   { background: '#fff', borderRadius: 12, padding: 28, width: 560, maxHeight: '90vh', overflowY: 'auto' },
  };

  return (
    <div style={sty.page}>
      <h1 style={sty.h1}>Empresas Contables</h1>
      <p style={sty.sub}>Gestiona los clientes del despacho contable. Selecciona una empresa para trabajar en su contabilidad.</p>

      {/* Barra de uso del plan */}
      <div style={{ background: '#fff', borderRadius: 10, padding: '14px 20px', marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,.06)', display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
        <div>
          <span style={{ fontSize: 13, color: '#64748b' }}>Empresas activas: </span>
          <strong style={{ fontSize: 15 }}>{uso}</strong>
          <span style={{ fontSize: 13, color: '#64748b' }}> / </span>
          <strong style={{ fontSize: 15, color: limite === -1 ? '#0f766e' : uso >= limite ? '#dc2626' : '#1e3a5f' }}>
            {limite === -1 ? 'Ilimitado' : limite}
          </strong>
        </div>
        <div style={{ flex: 1, background: '#e2e8f0', borderRadius: 99, height: 8, minWidth: 120, display: limite === -1 ? 'none' : 'block' }}>
          <div style={{ width: `${Math.min(100, (uso / Math.max(limite,1)) * 100)}%`, background: uso >= limite ? '#dc2626' : '#1e3a5f', borderRadius: 99, height: 8, transition: 'width .3s' }} />
        </div>
        <div style={{ fontSize: 12, color: '#64748b' }}>
          Plan: <strong>{limite === 1 ? 'Basico' : limite === 3 ? 'Profesional' : limite === 10 ? 'Empresarial' : limite === -1 ? 'Ilimitado' : `${limite} empresas`}</strong>
          {' · '}Para cambiar el plan ve a <a href="/configuracion" style={{ color: '#2563eb' }}>Configuracion</a>
        </div>
        <button
          style={{ ...sty.btn(uso >= limite && limite !== -1 ? '#94a3b8' : '#1e3a5f'), cursor: uso >= limite && limite !== -1 ? 'not-allowed' : 'pointer' }}
          onClick={uso >= limite && limite !== -1 ? undefined : abrirCrear}
          title={uso >= limite && limite !== -1 ? `Limite alcanzado. Plan actual: ${limite} empresa${limite===1?'':'s'}` : ''}
        >
          + Nueva Empresa
        </button>
      </div>

      <div style={sty.card}>
        {loading ? <p>Cargando...</p> : (
          <table style={sty.tbl}>
            <thead>
              <tr>
                <th style={sty.th}>Empresa</th>
                <th style={sty.th}>RNC</th>
                <th style={sty.th}>Tipo</th>
                <th style={sty.th}>Contacto</th>
                <th style={sty.th}>PIN</th>
                <th style={sty.th}>Estado</th>
                <th style={sty.th}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {empresas.map(emp => (
                <tr key={emp.id} style={emp.id === empresaActiva ? { background: '#f0fdf4' } : {}}>
                  <td style={sty.td}>
                    <div style={{ fontWeight: 700, color: '#1e3a5f' }}>{emp.nombre}</div>
                    {emp.id === empresaActiva && <span style={{ fontSize: 10, color: '#16a34a', fontWeight: 700 }}>EMPRESA ACTIVA</span>}
                  </td>
                  <td style={sty.td}>{emp.rnc || '—'}</td>
                  <td style={sty.td}>
                    <span style={sty.badge('#6366f1')}>{emp.tipo_empresa}</span>
                  </td>
                  <td style={sty.td}>
                    <div>{emp.contacto || '—'}</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>{emp.email || ''}</div>
                  </td>
                  <td style={sty.td}>
                    {emp.tiene_pin
                      ? <span style={sty.badge('#16a34a')}>🔐 Configurado</span>
                      : <span style={sty.badge('#dc2626')}>⚠ Sin PIN</span>
                    }
                  </td>
                  <td style={sty.td}>
                    <span style={sty.badge(emp.activa ? '#16a34a' : '#dc2626')}>
                      {emp.activa ? 'Activa' : 'Inactiva'}
                    </span>
                  </td>
                  <td style={sty.td}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button style={{ ...sty.btn('#0f766e'), padding: '4px 10px', fontSize: 11 }}
                        onClick={() => seleccionarEmpresa(emp)}>
                        Seleccionar
                      </button>
                      <button style={{ ...sty.btn('#7c3aed'), padding: '4px 10px', fontSize: 11 }}
                        onClick={() => setModalConfigPin(emp)}>
                        {emp.tiene_pin ? '🔐 PIN' : '⚙ Config PIN'}
                      </button>
                      <button style={{ ...sty.btn('#2563eb'), padding: '4px 10px', fontSize: 11 }}
                        onClick={() => abrirEditar(emp)}>
                        Editar
                      </button>
                      {emp.activa && (
                        <button style={{ ...sty.btn('#dc2626'), padding: '4px 10px', fontSize: 11 }}
                          onClick={() => desactivar(emp)}>
                          Desactivar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {empresas.length === 0 && (
                <tr><td colSpan={7} style={{ ...sty.td, textAlign: 'center', color: '#94a3b8', padding: 30 }}>
                  No hay empresas registradas
                </td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal crear/editar empresa */}
      {modalOpen && (
        <div style={sty.overlay} onClick={e => { if (e.target === e.currentTarget) setModalOpen(false); }}>
          <div style={sty.modal}>
            <h3 style={{ margin: '0 0 20px', fontSize: 18 }}>{editando ? 'Editar Empresa' : 'Nueva Empresa Contable'}</h3>

            <div style={sty.grid2}>
              <F label="Nombre *" field="nombre" placeholder="Nombre de la empresa" form={form} setForm={setForm} />
              <F label="RNC" field="rnc" placeholder="000-000000-0" form={form} setForm={setForm} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={sty.label}>Tipo de Empresa</label>
              <select style={{ ...sty.input }} value={form.tipo_empresa}
                onChange={e => setForm(f => ({ ...f, tipo_empresa: e.target.value }))}>
                {TIPOS.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
              </select>
            </div>
            <div style={sty.grid2}>
              <F label="Email" field="email" type="email" form={form} setForm={setForm} />
              <F label="Telefono" field="telefono" form={form} setForm={setForm} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <F label="Direccion" field="direccion" form={form} setForm={setForm} />
            </div>
            <div style={sty.grid2}>
              <F label="Persona de Contacto" field="contacto" form={form} setForm={setForm} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={sty.label}>Notas</label>
              <textarea style={{ ...sty.input, height: 70, resize: 'vertical' }}
                value={form.notas} onChange={e => setForm(f => ({ ...f, notas: e.target.value }))} />
            </div>

            {/* Logo de la empresa — se imprime en las facturas */}
            <div style={{ marginBottom: 12 }}>
              <label style={sty.label}>Logo (PNG, JPG o WebP · máx. 2 MB)</label>
              <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                <div style={{ width: 90, height: 90, border: '1px dashed #cbd5e1', borderRadius: 8,
                              display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                  {logoPreview
                    ? <img src={logoPreview} alt="preview" style={{ maxWidth: '100%', maxHeight: '100%' }} />
                    : logoActual
                      ? <img src={`/uploads/${logoActual.path}?v=${logoActual.v}`} alt="logo" style={{ maxWidth: '100%', maxHeight: '100%' }} />
                      : <span style={{ color: '#94a3b8', fontSize: 11 }}>Sin logo</span>}
                </div>
                <input type="file" accept="image/png,image/jpeg,image/webp" onChange={onLogoChange} />
              </div>
            </div>

            {/* PIN — solo al crear */}
            {!editando && (
              <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
                <p style={{ margin: '0 0 12px', fontSize: 13, color: '#0369a1', fontWeight: 600 }}>
                  🔐 PIN de acceso (obligatorio)
                </p>
                <div style={sty.grid2}>
                  <div>
                    <label style={sty.label}>PIN de 4 dígitos *</label>
                    <input
                      style={{ ...sty.input, fontSize: 20, letterSpacing: 8, textAlign: 'center', fontFamily: 'monospace', fontWeight: 700 }}
                      type="password" inputMode="numeric" maxLength={4} placeholder="••••"
                      value={form.pin}
                      onChange={e => setForm(f => ({ ...f, pin: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                    />
                  </div>
                  <div>
                    <label style={sty.label}>Confirmar PIN *</label>
                    <input
                      style={{ ...sty.input, fontSize: 20, letterSpacing: 8, textAlign: 'center', fontFamily: 'monospace', fontWeight: 700 }}
                      type="password" inputMode="numeric" maxLength={4} placeholder="••••"
                      value={form.pin_confirmar}
                      onChange={e => setForm(f => ({ ...f, pin_confirmar: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                    />
                  </div>
                </div>
                <p style={{ margin: '8px 0 0', fontSize: 11, color: '#64748b' }}>
                  El PIN se solicita cada vez que alguien cambia a esta empresa. Se guarda cifrado y nunca se muestra.
                </p>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button style={sty.btn('#64748b')} onClick={() => setModalOpen(false)}>Cancelar</button>
              <button style={sty.btn('#1e3a5f')} onClick={guardar}>Guardar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal PIN para seleccionar empresa */}
      {modalPin && (
        <ModalPin
          empresa={modalPin}
          onExito={onPinExito}
          onCancelar={() => setModalPin(null)}
        />
      )}

      {/* Modal configurar/cambiar PIN */}
      {modalConfigPin && (
        <ModalConfigurarPin
          empresa={modalConfigPin}
          onExito={onConfigPinExito}
          onCancelar={() => setModalConfigPin(null)}
        />
      )}
    </div>
  );
}
